import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import type { KnowledgeDocument } from "@chat-yai/shared/admin-types";
import { knowledgeApi } from "@/api/endpoints/knowledge";
import { Modal } from "@/components/Modal";
import { FormError } from "@/components/FormError";
import { Icon } from "@/components/Icon";
import { useToast } from "@/contexts/ToastContext";
import { ApiError } from "@/api/client";
import { translateError } from "@/i18n/errors";
import { permissionTooltip } from "@/lib/rbac";
import { EmptyState } from "@/components/EmptyState";

const KNOWLEDGE_MAX = 32 * 1024;

const baseSchema = z.object({
  kind: z.enum(["text", "faq"]),
  title: z.string().trim().min(1, "Title is required.").max(160, "Title too long."),
  content: z.string().max(KNOWLEDGE_MAX, "Content exceeds 32 KB."),
  faq_question: z.string().optional(),
  faq_answer: z.string().optional(),
});

const createSchema = baseSchema.superRefine((v, ctx) => {
  if (v.kind === "text") {
    if (!v.content.trim()) {
      ctx.addIssue({
        path: ["content"],
        code: z.ZodIssueCode.custom,
        message: "Content is required for text documents.",
      });
    }
  } else {
    if (!v.faq_question?.trim()) {
      ctx.addIssue({
        path: ["faq_question"],
        code: z.ZodIssueCode.custom,
        message: "Question is required.",
      });
    }
    if (!v.faq_answer?.trim()) {
      ctx.addIssue({
        path: ["faq_answer"],
        code: z.ZodIssueCode.custom,
        message: "Answer is required.",
      });
    }
  }
});

type CreateValues = z.infer<typeof baseSchema>;

interface Props {
  chatbotId: string;
  canEdit: boolean;
}

export function ChatbotKnowledgeTab({ chatbotId, canEdit }: Props): JSX.Element {
  const queryClient = useQueryClient();
  const toast = useToast();
  const [createOpen, setCreateOpen] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [confirmDelete, setConfirmDelete] = useState<KnowledgeDocument | null>(null);

  const listQuery = useQuery({
    queryKey: ["knowledge", chatbotId, { q: search, page }],
    queryFn: () =>
      knowledgeApi.list({
        chatbot_id: chatbotId,
        q: search || undefined,
        page,
        page_size: 20,
      }),
  });

  const form = useForm<CreateValues>({
    defaultValues: {
      kind: "text",
      title: "",
      content: "",
      faq_question: "",
      faq_answer: "",
    },
  });
  const kind = form.watch("kind");
  const content = form.watch("content");

  const createMutation = useMutation({
    mutationFn: (v: CreateValues) => {
      const parsed = createSchema.parse(v);
      // Backend expects faq fields only when kind=faq.
      const payload: Parameters<typeof knowledgeApi.create>[0] = {
        chatbot_id: chatbotId,
        kind: parsed.kind,
        title: parsed.title,
        content: parsed.kind === "faq" ? `${parsed.faq_question}\n\n${parsed.faq_answer}` : parsed.content,
        ...(parsed.kind === "faq"
          ? {
              faq_question: parsed.faq_question?.trim(),
              faq_answer: parsed.faq_answer?.trim(),
            }
          : {}),
      };
      return knowledgeApi.create(payload);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledge", chatbotId] });
      setCreateOpen(false);
      form.reset({ kind: "text", title: "", content: "", faq_question: "", faq_answer: "" });
      toast.push("success", "Knowledge added.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => knowledgeApi.remove(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["knowledge", chatbotId] });
      setConfirmDelete(null);
      toast.push("success", "Document removed.");
    },
    onError: (err) => {
      toast.push("error", translateError(err instanceof ApiError ? err.code : undefined));
    },
  });

  const items = listQuery.data?.items ?? [];
  const total = listQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / 20));

  return (
    <div style={{ marginTop: 16 }}>
      <div className="filters-bar">
        <div className="search-input">
          <Icon name="search" size={14} />
          <input
            value={search}
            onChange={(e) => {
              setSearch(e.target.value);
              setPage(1);
            }}
            placeholder="Search title or content"
            data-testid="knowledge-search"
          />
        </div>
        <div style={{ flex: 1 }} />
        {canEdit ? (
          <button
            className="btn btn-accent btn-sm"
            onClick={() => setCreateOpen(true)}
            data-testid="open-add-knowledge"
          >
            <Icon name="plus" size={12} /> Add document
          </button>
        ) : (
          <button
            className="btn btn-accent btn-sm"
            disabled
            title={permissionTooltip("write")}
          >
            <Icon name="plus" size={12} /> Add document
          </button>
        )}
      </div>

      <div className="card">
        <div
          className="table-row-head"
          style={{ gridTemplateColumns: "2fr 100px 100px 140px 100px" }}
        >
          <div>Title</div>
          <div>Kind</div>
          <div>Size</div>
          <div>Created</div>
          <div></div>
        </div>
        {items.map((d) => (
          <div
            key={d.id}
            className="list-row"
            style={{ gridTemplateColumns: "2fr 100px 100px 140px 100px" }}
            data-testid={`knowledge-row-${d.id}`}
          >
            <div>
              <div style={{ fontWeight: 500, fontSize: 13 }}>{d.title}</div>
              <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
                {d.kind === "faq" && d.faq_question
                  ? d.faq_question.slice(0, 80)
                  : d.content.slice(0, 80)}
                {d.content.length > 80 ? "…" : ""}
              </div>
            </div>
            <div>
              <span className="chip">{d.kind === "faq" ? "FAQ" : "Text"}</span>
            </div>
            <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
              {formatBytes(d.size_bytes)}
            </div>
            <div className="mono" style={{ fontSize: 11, color: "var(--text-3)" }}>
              {new Date(d.created_at).toLocaleDateString()}
            </div>
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              {canEdit ? (
                <button
                  className="btn btn-ghost btn-sm"
                  onClick={() => setConfirmDelete(d)}
                  data-testid={`delete-knowledge-${d.id}`}
                >
                  Delete
                </button>
              ) : (
                <button
                  className="btn btn-ghost btn-sm"
                  disabled
                  title={permissionTooltip("write")}
                >
                  Delete
                </button>
              )}
            </div>
          </div>
        ))}
        {items.length === 0 ? (
          search ? (
            <div className="empty">No documents match that search.</div>
          ) : (
            <EmptyState
              icon="doc"
              title="No knowledge documents yet"
              description="Add the FAQ and reference text the assistant should reach for. Up to 32 KB per document; we recommend short, scannable chunks."
              primary={
                canEdit
                  ? {
                      label: "Add your first document",
                      onClick: () => setCreateOpen(true),
                      testId: "empty-knowledge-add",
                    }
                  : undefined
              }
              testId="empty-knowledge"
            />
          )
        ) : null}
        {total > 20 ? (
          <div className="pagination">
            <span>
              Page {page} of {totalPages} · {total} documents
            </span>
            <div style={{ display: "flex", gap: 6 }}>
              <button
                className="btn btn-outline btn-sm"
                disabled={page <= 1}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                Previous
              </button>
              <button
                className="btn btn-outline btn-sm"
                disabled={page >= totalPages}
                onClick={() => setPage((p) => p + 1)}
              >
                Next
              </button>
            </div>
          </div>
        ) : null}
      </div>

      <Modal
        title="Add knowledge document"
        open={createOpen}
        onClose={() => setCreateOpen(false)}
        footer={
          <>
            <button className="btn btn-ghost btn-sm" onClick={() => setCreateOpen(false)}>
              Cancel
            </button>
            <button
              className="btn btn-primary btn-sm"
              disabled={createMutation.isPending}
              onClick={form.handleSubmit((v) => createMutation.mutate(v))}
              data-testid="submit-add-knowledge"
            >
              {createMutation.isPending ? "Saving…" : "Save"}
            </button>
          </>
        }
      >
        <div className="field-group">
          <label>Document type</label>
          <select
            className="field-input"
            data-testid="knowledge-kind"
            {...form.register("kind")}
          >
            <option value="text">Text · paste any context</option>
            <option value="faq">FAQ · question and answer pair</option>
          </select>
        </div>
        <div className="field-group">
          <label>Title</label>
          <input className="field-input" {...form.register("title")} data-testid="knowledge-title" />
          <FormError message={form.formState.errors.title?.message} />
        </div>
        {kind === "text" ? (
          <div className="field-group">
            <label>Content (max 32 KB)</label>
            <textarea
              className="field-input"
              rows={8}
              data-testid="knowledge-content"
              {...form.register("content")}
            />
            <div className="help">
              {(content?.length ?? 0).toLocaleString()} / {KNOWLEDGE_MAX.toLocaleString()} chars
            </div>
            <FormError message={form.formState.errors.content?.message} />
          </div>
        ) : (
          <>
            <div className="field-group">
              <label>Question</label>
              <input
                className="field-input"
                data-testid="knowledge-faq-q"
                {...form.register("faq_question")}
              />
              <FormError message={form.formState.errors.faq_question?.message} />
            </div>
            <div className="field-group">
              <label>Answer</label>
              <textarea
                className="field-input"
                rows={4}
                data-testid="knowledge-faq-a"
                {...form.register("faq_answer")}
              />
              <FormError message={form.formState.errors.faq_answer?.message} />
            </div>
          </>
        )}
      </Modal>

      <Modal
        title="Delete document"
        open={!!confirmDelete}
        onClose={() => setConfirmDelete(null)}
        footer={
          <>
            <button className="btn btn-ghost btn-sm" onClick={() => setConfirmDelete(null)}>
              Cancel
            </button>
            <button
              className="btn btn-danger btn-sm"
              disabled={deleteMutation.isPending}
              onClick={() => confirmDelete && deleteMutation.mutate(confirmDelete.id)}
            >
              {deleteMutation.isPending ? "Deleting…" : "Delete"}
            </button>
          </>
        }
      >
        {confirmDelete ? (
          <p>
            Remove <strong>{confirmDelete.title}</strong> from the chatbot&apos;s knowledge?
          </p>
        ) : null}
      </Modal>
    </div>
  );
}

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / 1024 / 1024).toFixed(2)} MB`;
}
